/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  PlusCircle,
  Search,
  Edit2,
  Trash2,
  Filter,
  X,
  Layers,
  Lock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { roleService } from '../services/roleService';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  scope: 'All',
};

const SCOPE_LABELS = {
  platform: 'Platform Wide',
  account: `${ACCOUNT_LABEL.singular} Level`,
  sub_account: `${SUB_ACCOUNT_LABEL.singular} Level`,
};

const Roles = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user, userScope } = useAuth();

  const currentUserScope = user?.scope || userScope || 'sub_account';
  const isPlatformScope = currentUserScope === 'platform';
  const isAccountScope = currentUserScope === 'account';
  const isScopeFilterVisible = isPlatformScope || isAccountScope;

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset filter scope if it's not valid for the current user's scope
  useEffect(() => {
    if (!isScopeFilterVisible) {
      if (filters.scope !== 'All') {
        setFilters((f) => ({ ...f, scope: 'All' }));
      }
    } else if (isAccountScope && filters.scope === 'platform') {
      setFilters((f) => ({ ...f, scope: 'All' }));
    }
  }, [isScopeFilterVisible, isAccountScope, filters.scope]);

  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchRoles = useCallback(async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
      };

      if (debouncedFilters.search) {
        params.search = debouncedFilters.search;
      }
      if (isScopeFilterVisible && debouncedFilters.scope && debouncedFilters.scope !== 'All') {
        params.scope = debouncedFilters.scope;
      }

      const response = await roleService.getRoles(params);
      if (currentRequestId !== requestIdRef.current) return;

      const rolesData = response.data?.roles || (Array.isArray(response.data) ? response.data : []);
      const total = response.data?.pagination?.total ?? response.data?.total ?? rolesData.length;
      const limit = response.data?.pagination?.limit ?? response.data?.limit ?? currentPagination.limit;
      const totalPages = response.data?.pagination?.totalPages ?? response.data?.totalPages ?? Math.ceil(total / limit);

      setRoles(rolesData);
      setPagination({
        page: response.data?.pagination?.page ?? response.data?.page ?? (pageOverride ?? currentPagination.page),
        limit,
        total,
        totalPages,
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load roles');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedFilters, isScopeFilterVisible]);

  const isFirstFilterFetchRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterFetchRender.current) {
      isFirstFilterFetchRender.current = false;
      return;
    }
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [debouncedFilters, isScopeFilterVisible]);

  useEffect(() => {
    fetchRoles(pagination.page);
  }, [pagination.page, fetchRoles]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleOpenDelete = (role) => {
    if (role.isSystem) {
      toastService.info('System roles cannot be deleted');
      return;
    }
    setDeleteTarget(role);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await roleService.deleteRole(deleteTarget.id);
      toastService.success('Role deleted successfully');
      setDeleteTarget(null);
      fetchRoles(pagination.page);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete role';
      toastService.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    (isScopeFilterVisible && filters.scope !== 'All')
  );

  const canManage = can.roles.update() || can.roles.delete();
  // System roles are editable only by platform-scoped users with roles.update (enforced server-side too)
  const canEditSystemRoles = isPlatformScope && can.roles.update();
  // Locked roles (e.g. Platform Admin) are managed by rbac-sync and never editable
  const canEditRole = (role) => !role.isLocked && can.roles.update() && (!role.isSystem || canEditSystemRoles);

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Roles & Permissions
            </h1>
            <p className="text-xs text-base-content/50">
              Define access control roles, assign administrative scopes, and manage system permissions.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {can.roles.create() && (
              <button
                onClick={() => navigate('/roles/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New Role</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        {/* Main Search and Filter Grid */}
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative col-span-12 ${isScopeFilterVisible ? 'sm:col-span-8 lg:col-span-9' : ''}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by role name or key..."
              aria-label="Search by role name or key"
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Scope Filter */}
          {isScopeFilterVisible && (
            <div className="col-span-12 sm:col-span-4 lg:col-span-3">
              <select
                aria-label="Filter by scope"
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.scope}
                onChange={(e) => setFilters(f => ({ ...f, scope: e.target.value }))}
              >
                <option value="All">All Scopes</option>
                {isPlatformScope && <option value="platform">Platform Wide</option>}
                <option value="account">{ACCOUNT_LABEL.singular} Level</option>
                <option value="sub_account">{SUB_ACCOUNT_LABEL.singular} Level</option>
              </select>
            </div>
          )}
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {isScopeFilterVisible && filters.scope !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Layers size={10} className="text-primary" />
                  <span>Scope: {SCOPE_LABELS[filters.scope] || filters.scope}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, scope: 'All' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : roles.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <Shield size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? 'No matching roles' : 'No roles yet'}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria, scope filter, or clearing active filters.'
                  : 'Get started by creating your first custom access role.'}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : can.roles.create() ? (
              <button
                type="button"
                onClick={() => navigate('/roles/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create New Role</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">Display Name</th>
                  <th className="py-3.5 px-4">Role Key</th>
                  <th className="py-3.5 px-4">Scope</th>
                  <th className="py-3.5 px-4">Type</th>
                  {canManage && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-base-200/40 transition-colors group">
                    {/* Display Name */}
                    <td className="py-3.5 px-5 font-bold">
                      <div className="flex items-center gap-2">
                        {canEditRole(role) ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/roles/${role.id}/edit`)}
                            className="text-base-content group-hover:text-primary hover:underline font-bold text-sm tracking-tight text-left transition-colors"
                          >
                            {role.displayName}
                          </button>
                        ) : (
                          <span className="text-base-content font-bold text-sm tracking-tight">
                            {role.displayName}
                          </span>
                        )}
                        {role.isDefault && (
                          <span className="badge badge-primary badge-outline text-[9px] h-4 py-0 px-1.5 font-black uppercase tracking-wider">
                            Default
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Role Key */}
                    <td className="py-3.5 px-4">
                      <code className="text-[11px] font-bold text-base-content/70 uppercase tracking-tight bg-base-200/70 border border-base-content/5 px-2 py-0.5 rounded-lg font-mono">
                        {role.name}
                      </code>
                    </td>

                    {/* Scope Badge */}
                    <td className="py-3.5 px-4">
                      <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-xl border inline-flex items-center gap-1 ${
                        role.scope === 'platform'
                          ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20'
                          : role.scope === 'account'
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                      }`}>
                        {role.scope === 'sub_account' || !role.scope ? SUB_ACCOUNT_LABEL.singular : role.scope === 'account' ? ACCOUNT_LABEL.singular : role.scope.replace('_', ' ')}
                      </span>
                    </td>

                    {/* Role Type */}
                    <td className="py-3.5 px-4">
                      <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-xl border inline-flex items-center gap-1 ${
                        role.isSystem
                          ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                          : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                      }`}>
                        {role.isSystem ? (
                          <>
                            <Lock size={10} />
                            <span>System</span>
                          </>
                        ) : (
                          <span>Custom</span>
                        )}
                      </span>
                    </td>

                    {/* Actions */}
                    {canManage && (
                        <td className="py-3.5 px-5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEditRole(role) || !role.isSystem ? (
                              <>
                                {canEditRole(role) && (
                                  <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Edit Role">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/roles/${role.id}/edit`)}
                                      className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                      aria-label="Edit Role"
                                    >
                                      <Edit2 size={15} />
                                    </button>
                                  </div>
                                )}
                                {!role.isSystem && can.roles.delete() && (
                                  <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete Role">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenDelete(role)}
                                      className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                      aria-label="Delete Role"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="tooltip tooltip-left tooltip-neutral before:text-xs before:font-semibold before:whitespace-nowrap" data-tip={role.isLocked ? 'Locked role — not editable' : 'System role protected'}>
                                <span className="p-1.5 text-base-content/20 inline-flex items-center cursor-default">
                                  <Lock size={15} />
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && roles.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> records
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Role"
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete the role{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {deleteTarget?.displayName}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. All users assigned to this role will lose their current access permissions immediately.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete Role'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Roles;